import React from 'react'
import styled from 'styled-components'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { media, mediaMax } from 'styles/media'

import useReduxSelector from 'hooks/useReduxSelector'

import UnreadIndicator from '../../../inbox/UnreadIndicator'

import IncompleteBooking from '../IncompleteBooking'

import {
  DisplayOnlyAtBreakpoint,
  Divider,
  DropdownWrapper,
  NavLinkAnchor,
  NavLinkOrAnchor,
  NavLinkLoading,
} from '../common'
import { InverseOption } from '../types'

import Dropdown, { LinkList } from './Dropdown'

const LargerThanMdOnly = styled(DisplayOnlyAtBreakpoint)`
  ${media.md`
    display: inline-flex;
  `}
`

const LargerThanLgOnly = styled(DisplayOnlyAtBreakpoint)`
  ${media.lg`
    display: inline-flex;
  `}
`

const LargerThanXlOnly = styled(DisplayOnlyAtBreakpoint)`
  ${media.xl`
    display: inline-flex;
  `}
`

const SmallerThanXlOnly = styled(DisplayOnlyAtBreakpoint)`
  ${mediaMax.xl`
    display: inline-block;
  `}
`

export const LoggedInLinks = ({ inverse }: InverseOption) => {
  const {
    avatar_url: avatarUrl,
    unread_conversations_count: unreadCount,
    host,
    storyteller,
    admin,
    eligible_for_referrals: eligibleForReferrals,
  } = useReduxSelector((state) => state?.auth?.currentUser)

  return (
    <>
      {!host && (
        <>
          <NavLinkOrAnchor inverse={inverse} to="/my/bucketlists">
            Bucket Lists
          </NavLinkOrAnchor>
          <NavLinkOrAnchor inverse={inverse} to="/my/bookings">
            Booked Escapes
          </NavLinkOrAnchor>
        </>
      )}
      {host && (
        <NavLinkOrAnchor inverse={inverse} to="/my/listings">
          My Listings
        </NavLinkOrAnchor>
      )}
      {storyteller && (
        <NavLinkOrAnchor inverse={inverse} to="/my/assignments">
          Story Assignments
        </NavLinkOrAnchor>
      )}
      <NavLinkOrAnchor inverse={inverse} to="/my/inbox">
        Inbox
        <UnreadIndicator unreadCount={unreadCount} />
      </NavLinkOrAnchor>

      <DropdownWrapper>
        <Dropdown avatarUrl={avatarUrl} inverse={inverse} signedIn>
          <div style={{ display: 'flex', gap: '16px' }}>
            <LinkList>
              <li>
                <NavLinkOrAnchor to="/my/profile/edit">Profile</NavLinkOrAnchor>
              </li>
              {host && (
                <>
                  <li>
                    <NavLinkOrAnchor to="/my/bucketlists">
                      Bucket Lists
                    </NavLinkOrAnchor>
                  </li>
                  <li>
                    <NavLinkOrAnchor to="/my/bookings">
                      Booked Escapes
                    </NavLinkOrAnchor>
                  </li>
                </>
              )}
              {eligibleForReferrals && (
                <li>
                  <NavLinkOrAnchor to="/my/referrals">
                    Refer a Friend
                  </NavLinkOrAnchor>
                </li>
              )}
              <li>
                <NavLinkOrAnchor to="/my/account">Account</NavLinkOrAnchor>
              </li>

              <Divider />

              <li>
                <NavLinkOrAnchor to="/vouchers">Gift Vouchers</NavLinkOrAnchor>
              </li>

              <li>
                <NavLinkOrAnchor to="/list-with-us">
                  Become a Host
                </NavLinkOrAnchor>
              </li>
              <li>
                <NavLinkAnchor
                  href="https://help.riparide.com"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Help and FAQs
                </NavLinkAnchor>
              </li>
              <li>
                <NavLinkOrAnchor to="/about">About</NavLinkOrAnchor>
              </li>

              <Divider />

              <li>
                <NavLinkAnchor href="/users/sign_out">Log Out</NavLinkAnchor>
              </li>

              {admin && (
                <li>
                  <NavLinkAnchor href="/admin">Admin</NavLinkAnchor>
                </li>
              )}
            </LinkList>

            <IncompleteBooking />
          </div>
        </Dropdown>
      </DropdownWrapper>
    </>
  )
}

export const LoggedOutLinks = ({ inverse }: InverseOption) => {
  const { lg: largerThanLg } = useMediaQueryContext()
  const smallerThanLg = !largerThanLg

  return (
    <>
      <LargerThanMdOnly>
        <NavLinkOrAnchor inverse={inverse} to="/listings?type=accommodation">
          Book an Escape
        </NavLinkOrAnchor>
      </LargerThanMdOnly>

      <LargerThanLgOnly>
        <NavLinkOrAnchor inverse={inverse} to="/about">
          About
        </NavLinkOrAnchor>
      </LargerThanLgOnly>

      <LargerThanLgOnly>
        <NavLinkOrAnchor inverse={inverse} to="/vouchers">
          Gift Vouchers
        </NavLinkOrAnchor>
      </LargerThanLgOnly>

      <NavLinkOrAnchor inverse={inverse} to="/list-with-us">
        Become a Host
      </NavLinkOrAnchor>

      <LargerThanXlOnly>
        <NavLinkAnchor inverse={inverse} href="/signup">
          Sign Up
        </NavLinkAnchor>
      </LargerThanXlOnly>

      <LargerThanXlOnly>
        <NavLinkAnchor inverse={inverse} href="/login">
          Login
        </NavLinkAnchor>
      </LargerThanXlOnly>

      <SmallerThanXlOnly>
        <Dropdown inverse={inverse} signedIn={false}>
          <LinkList>
            {smallerThanLg && (
              <>
                <li>
                  <NavLinkOrAnchor to="/listings?type=accommodation">
                    Book an Escape
                  </NavLinkOrAnchor>
                </li>
                <li>
                  <NavLinkOrAnchor to="/vouchers">
                    Gift Vouchers
                  </NavLinkOrAnchor>
                </li>
                <li>
                  <NavLinkOrAnchor to="/about">About</NavLinkOrAnchor>
                </li>
              </>
            )}

            <li>
              <NavLinkAnchor href="/signup">Sign Up</NavLinkAnchor>
            </li>
            <li>
              <NavLinkAnchor href="/login">Login</NavLinkAnchor>
            </li>
          </LinkList>
        </Dropdown>
      </SmallerThanXlOnly>
    </>
  )
}

export const LinksLoadingSkeleton = () => (
  <>
    <NavLinkLoading />
    <NavLinkLoading />
    <LargerThanLgOnly>
      <NavLinkLoading />
    </LargerThanLgOnly>
  </>
)
